/**
 * AuthContext.js — Authentication State & Provider
 *
 * หน้าที่ของไฟล์นี้: เก็บ state + orchestrate เท่านั้น
 *
 * สิ่งที่แยกออกไปแล้ว:
 *   HTTP Layer      → src/lib/authClient.js
 *   API Functions   → src/services/authService.js
 *   Token Timers    → src/hooks/useTokenManager.js
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { apiClient, configureAuthClient, refreshWithCrossTabLock } from '../lib/authClient';
import { authService } from '../services/authService';
import { useTokenManager } from '../hooks/useTokenManager';

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext(null);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TOKEN_EXPIRY_MARGIN = 30 * 1000; // 30s safety margin

const decodeJwt = (token) => {
  try { return JSON.parse(atob(token.split('.')[1])); }
  catch { return null; }
};

const isTokenExpired = (token) => {
  const p = decodeJwt(token);
  if (!p?.exp) return false;
  return Date.now() >= (p.exp * 1000 - TOKEN_EXPIRY_MARGIN);
};

/** แปลง navigation response
 * - grouped: Array ของ groups สำหรับ Layout sidebar [ {group_id, group_title, items:[...]} ]
 * - flat:    Array ของทุก item สำหรับ hasPermission
 */
const parseNavigation = (navData) => {
  const raw = navData?.data ?? navData;
  if (!Array.isArray(raw)) return { groups: [], flat: [] };

  // Backend ส่งเป็น grouped array แล้ว
  if (raw.length > 0 && 'items' in raw[0]) {
    const flat = raw.flatMap((g) => g.items || []);
    return { groups: raw, flat };
  }

  // Fallback: flat array ไม่มี items
  return { groups: [], flat: raw };
};

// ─── AuthProvider ─────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }) => {
  // status: 'initializing' | 'authenticated' | 'unauthenticated'
  const [status, setStatus] = useState('initializing');
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [navigation, setNavigation] = useState([]); // grouped — สำหรับ Layout sidebar
  const [permissions, setPermissions] = useState([]); // flat    — สำหรับ hasPermission

  // ป้องกัน React Strict Mode double-invoke
  const initDoneRef = useRef(false);

  // BroadcastChannel — sync token ข้าม tab ใน browser เดียวกัน
  const broadcastRef = useRef(null);

  // ─── Force Logout ─────────────────────────────────────────────────────────
  const forceLogout = useCallback((reason = null) => {
    console.warn('🚪 forceLogout:', reason || 'session expired');
    setUser(null);
    setToken(null);
    setNavigation([]);
    setPermissions([]);
    setStatus('unauthenticated');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    delete apiClient.defaults.headers.Authorization;

    broadcastRef.current?.postMessage({ type: 'LOGGED_OUT' });

    if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/public/')) {
      const url = new URL('/login', window.location.origin);
      if (reason) url.searchParams.set('reason', reason);
      window.location.href = url.toString();
    }
  }, []);

  // ─── Token Manager (timers + listeners) ──────────────────────────────────
  const { scheduleProactiveRefresh, startHeartbeat, stopAll } = useTokenManager({
    onTokensRefreshed: ({ access_token, refresh_token }) => {
      // อัพเดท axios default header + แจ้ง tab อื่น
      setToken(access_token);
      apiClient.defaults.headers.Authorization = `Bearer ${access_token}`;
      broadcastRef.current?.postMessage({
        type: 'TOKEN_REFRESHED',
        access_token,
        refresh_token,
      });
    },
    onForceLogout: forceLogout,
  });

  // ─── Configure authClient (inject React callbacks) ────────────────────────
  // เรียกครั้งเดียวตอน mount เพื่อให้ interceptor รู้จัก forceLogout
  useEffect(() => {
    configureAuthClient({
      getRefreshToken: () => localStorage.getItem('refreshToken'),
      onRefreshSuccess: ({ access_token, refresh_token }) => {
        apiClient.defaults.headers.Authorization = `Bearer ${access_token}`;
        broadcastRef.current?.postMessage({ type: 'TOKEN_REFRESHED', access_token, refresh_token });
      },
      onForceLogout: forceLogout,
    });
  }, [forceLogout]);

  // ─── BroadcastChannel (Tab Sync) ─────────────────────────────────────────
  useEffect(() => {
    if (!('BroadcastChannel' in window)) return;
    const ch = new BroadcastChannel('kitcrm_auth');
    broadcastRef.current = ch;

    ch.onmessage = ({ data }) => {
      if (!data?.type) return;

      if (data.type === 'TOKEN_REFRESHED') {
        // Tab อื่น refresh สำเร็จ — ซิงก์ token โดยไม่เรียก API
        console.log('📡 [TabSync] Token synced from another tab');
        localStorage.setItem('token', data.access_token);
        setToken(data.access_token);
        if (data.refresh_token) localStorage.setItem('refreshToken', data.refresh_token);
        apiClient.defaults.headers.Authorization = `Bearer ${data.access_token}`;
        if (data.access_token) scheduleProactiveRefresh(data.access_token);
      }

      if (data.type === 'LOGGED_OUT') {
        console.warn('📡 [TabSync] Logged out from another tab');
        setUser(null);
        setToken(null);
        setNavigation([]);
        setPermissions([]);
        setStatus('unauthenticated');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    };

    return () => { ch.close(); broadcastRef.current = null; };
  }, [scheduleProactiveRefresh]);

  // ─── Init Auth ────────────────────────────────────────────────────────────
  useEffect(() => {
    // ป้องกัน React Strict Mode รัน 2 ครั้ง (dev double-invoke)
    if (initDoneRef.current) return;
    initDoneRef.current = true;

    const init = async () => {
      const storedToken = localStorage.getItem('token');
      const storedRt = localStorage.getItem('refreshToken');

      console.log('🚀 initAuth: checking session...');

      if (!storedToken || !storedRt) {
        console.log('🚀 initAuth: no tokens → unauthenticated');
        setStatus('unauthenticated');
        return;
      }

      let activeToken = storedToken;

      // Token หมดอายุ → refresh ก่อน
      if (isTokenExpired(storedToken)) {
        console.warn('⚠️ initAuth: token expired → refreshing...');
        try {
          const { data } = await refreshWithCrossTabLock(storedRt);
          if (data.error_code) throw new Error(data.error_code);
          activeToken = data.access_token;
          localStorage.setItem('token', activeToken);
          if (data.refresh_token) localStorage.setItem('refreshToken', data.refresh_token);
        } catch (err) {
          console.error('❌ initAuth: refresh failed →', err.message);
          setStatus('unauthenticated');
          forceLogout();
          return;
        }
      }

      // แนบ token ให้ apiClient
      apiClient.defaults.headers.Authorization = `Bearer ${activeToken}`;

      // โหลด user + permissions พร้อมกัน
      try {
        const [meRes, navRes] = await Promise.all([
          authService.getMe(),
          authService.getNavigation(),
        ]);

        const userData = meRes.data?.data || meRes.data;
        const { reset_token, reset_token_expires, ...safeUser } = userData;
        setUser(safeUser);
        const nav = parseNavigation(navRes.data);
        setNavigation(nav.groups);
        setPermissions(nav.flat);
        console.log('✅ initAuth: session restored');

      } catch (err) {
        console.error('❌ initAuth: failed to load user data:', err.message);
        // Auth error → logout
        if (err.response?.status === 401 || err.response?.status === 403) {
          forceLogout();
          return;
        }
        // Network/timeout → ไม่ logout ให้ user retry เอง
      }

      setStatus('authenticated');
      setToken(activeToken);
      scheduleProactiveRefresh(activeToken);
      startHeartbeat();
    };

    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Login ────────────────────────────────────────────────────────────────
  const login = useCallback(async (userData, accessToken, refreshTokenValue) => {
    const { reset_token, reset_token_expires, ...safeUser } = userData;

    localStorage.setItem('token', accessToken);
    localStorage.setItem('refreshToken', refreshTokenValue);
    apiClient.defaults.headers.Authorization = `Bearer ${accessToken}`;

    // โหลด permissions
    try {
      const navRes = await authService.getNavigation();
      const nav = parseNavigation(navRes.data);
      setNavigation(nav.groups);
      setPermissions(nav.flat);
    } catch (err) {
      console.warn('login: failed to load navigation', err.message);
    }

    setUser(safeUser);
    setToken(accessToken);
    setStatus('authenticated');
    scheduleProactiveRefresh(accessToken);
    startHeartbeat();
  }, [scheduleProactiveRefresh, startHeartbeat]);

  // ─── Logout ───────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    stopAll();
    const rt = localStorage.getItem('refreshToken');
    try {
      if (rt) await authService.logout(rt);
    } catch (err) {
      console.warn('logout API failed (non-critical):', err.message);
    }

    setUser(null);
    setToken(null);
    setNavigation([]);
    setPermissions([]);
    setStatus('unauthenticated');
    // localStorage.clear();  
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    delete apiClient.defaults.headers.Authorization;
  }, [stopAll]);

  // ─── Refresh User ─────────────────────────────────────────────────────────
  const refreshUser = useCallback(async () => {
    try {
      const [meRes, navRes] = await Promise.all([
        authService.getMe(),
        authService.getNavigation(),
      ]);
      const userData = meRes.data?.data || meRes.data;
      const { reset_token, reset_token_expires, ...safeUser } = userData;
      setUser(safeUser);
      const nav = parseNavigation(navRes.data);
      setNavigation(nav.groups);
      setPermissions(nav.flat);
    } catch (err) {
      console.error('refreshUser failed:', err);
    }
  }, []);

  // ─── Permission Helper ────────────────────────────────────────────────────
  const hasPermission = useCallback((menuLabelOrPath, action = 'view') => {
    if (!permissions?.length) return false;
    const target = menuLabelOrPath.toLowerCase();
    const menu = permissions.find((p) =>
      p.permission_key?.toLowerCase() === target ||
      p.label?.toLowerCase() === target ||
      p.path?.toLowerCase().includes(target) ||
      p.menu_name?.toLowerCase() === target
    );
    if (!menu) return false;
    if (action === 'view') {
      if (menu.permissions && typeof menu.permissions === 'object') {
        return menu.permissions.can_view !== undefined ? !!menu.permissions.can_view : true;
      }
      return menu.can_view !== undefined ? !!menu.can_view : true;
    }
    const field = `can_${action}`;
    return menu.permissions && typeof menu.permissions === 'object'
      ? !!menu.permissions[field]
      : !!menu[field];
  }, [permissions]);

  // ─── Context Value ────────────────────────────────────────────────────────
  const value = {
    user,
    token,
    navigation,   // grouped — [ {group_id, group_title, items:[...]} ] สำหรับ Layout
    permissions,  // flat    — [...items] สำหรับ hasPermission
    isLoading: status === 'initializing',
    isAuthenticated: status === 'authenticated',
    login,
    logout,
    refreshUser,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ─── Consumer Hook ────────────────────────────────────────────────────────────

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
